import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  CardHeader,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { CreateTenantRequest, UpdateTenantRequest } from '../../../app/services/api/generated';

const TenantForm = () => {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const { getTenantById, createTenant, updateTenant } = useNswagClient();
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const [name, setName] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchTenant = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const response = await getTenantById(parseInt(id));
      if (response?.data) {
        setName(response.data.name || '');
      }
    } catch (error) {
      console.error('Error fetching tenant:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchTenant();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!name?.trim()) {
      newErrors.name = t('tenants.nameRequired');
    } else if (name.trim().length > 64) {
      newErrors.name = t('tenants.nameTooLong');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const body: UpdateTenantRequest = { name: name.trim() } as UpdateTenantRequest;
        await updateTenant(parseInt(id!), body);
        setMessage(t('tenants.updateSuccess'));
        setIsSuccess(true);
        setShowMessage(true);
        setTimeout(() => {
          navigate('/admin/tenants');
        }, 500);
      } else {
        const body: CreateTenantRequest = { name: name.trim() } as CreateTenantRequest;
        await createTenant(body);
        setMessage(t('tenants.createSuccess'));
        setIsSuccess(true);
        setShowMessage(true);
        setTimeout(() => {
          navigate('/admin/tenants');
        }, 500);
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('tenants.updateError') : t('tenants.createError')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/tenants');
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'TenantUpdate' : 'TenantCreate']);
  const canView = hasPermissionTo(['TenantView']);

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('tenants.accessDenied')}</PageTitle>
        <Typography>{t('tenants.insufficientPermissions')}</Typography>
      </PageArea>
    );
  }

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('tenants.editTenant') : t('tenants.createNew')}</PageTitle>
        <Typography>{t('tenants.loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        <Card elevation={1}>
          <CardHeader
            title={isEdit ? t('tenants.editTenant') : t('tenants.createNew')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid
              container
              spacing={8}>
              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('tenants.name')}
                  placeholder={t('tenants.namePlaceholder')}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) {
                      setErrors(prev => ({ ...prev, name: '' }));
                    }
                  }}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  inputProps={{ maxLength: 64 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                {t('tenants.cancel')}
              </Button>
              {userCanSave && (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={{ minWidth: 120, borderRadius: 2 }}
                >
                  {isSaving ? t('tenants.saving') : isEdit ? t('tenants.update') : t('tenants.create')}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default TenantForm;
